import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import LoadingBar from '@/global/components/ui/loading-bar'
import ContentGuide from '@/global/guides/ContentGuide'
import { useStore } from '@/store'
import { useMemo } from 'react'
import { Outlet } from 'react-router-dom'
import DigitacaoErrorOverlay from '../ErrorOverlay'
import { filterJornada } from '../utils/filterJornada'
import { useDigitacaoStore } from '../utils/store'
import {
    getDigitacaoRowClassName,
    getDigitacaoRowHeight,
} from './digitacao-getters'
import { useDigitacaoContext } from './useDigitacaoContext'
import { useUpdateDigitacaoQueryData } from './useDigitacaoData'

interface IProps {
    columns: GridColDefPro[]
    isLoadingDiario: boolean
    isLoadingMensal: boolean
    isErrorMensal: boolean
    isErrorDiario: boolean
    diarioError: any
    mensalError: any
    dataMensal?: any[]
    dataDiario?: any[]
    Toolbar: () => React.ReactNode
}

export function DigitacaoSubPages({
    columns,
    dataMensal: mensal = [],
    dataDiario: diario = [],
    diarioError,
    isErrorDiario,
    isErrorMensal,
    isLoadingDiario,
    isLoadingMensal,
    mensalError,
    Toolbar,
}: IProps) {
    const {
        view,
        periodo: [dataInicial, dataFinal],
    } = useDigitacaoContext()
    const { setQueryData } = useUpdateDigitacaoQueryData()
    const setFuncionarioId = useStore(state => state.setEmployeeId)
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const isHorarioNoturno = useStore(
        state => state.employee?.horarioNoturno === 'S',
    )
    const organizacao = useDigitacaoStore(state => state.organizacao)

    const isLoading =
        (view == 'mensal' && isLoadingMensal) ||
        (view == 'diario' && isLoadingDiario)

    const MemoizedLoading = useMemo(
        () =>
            isLoading ? (
                <LoadingBar key="loading" className="border-x h-1" />
            ) : (
                <div className="border-x h-1" key="not-loading" />
            ),
        [isLoading],
    )

    const data = useMemo(() => {
        if ([funcionarioId, empresaId].includes(-1)) return []

        if (view === 'mensal') {
            if (!mensal.length) return []
            return mensal
                .filter(
                    view === 'mensal' && organizacao === 'jornada'
                        ? () => true
                        : filterJornada(isHorarioNoturno),
                )
                .map((registro, index) => ({
                    ...registro,
                    id: index + 1,
                }))
        }

        if (!diario.length) return []
        return diario
            .sort((a, b) => a.nome.localeCompare(b.nome))
            .map(({ id, ...rest }, index) => ({
                id: index + 1,
                funcionarioId: id,
                ...rest,
            }))
    }, [mensal, diario, funcionarioId, view, setQueryData])

    const error = useMemo(() => {
        if (isErrorDiario)
            return {
                isError: true,
                message: diarioError?.message,
            }
        if (isErrorMensal)
            return {
                isError: true,
                message: mensalError?.message,
            }
        if (!isLoading && !data.length)
            return {
                isError: true,
                message: 'Nenhum registro encontrado',
            }
        return {
            isError: false,
            message: '',
        }
    }, [isErrorMensal, isErrorDiario, isLoading, data.length])

    return [
        <Outlet
            key="outlet"
            context={{
                data,
                view,
                periodo: [dataInicial, dataFinal],
            }}
        />,
        MemoizedLoading,
        <ContentGuide.DataGrid
            key={view}
            loading={!data?.length && isLoading}
            rows={data}
            getRowHeight={getDigitacaoRowHeight}
            columns={columns}
            hideFooter
            components={{
                ErrorOverlay: () => (
                    <DigitacaoErrorOverlay message={error.message} />
                ),
            }}
            slots={{ Toolbar }}
            onRowClick={value => {
                if (view === 'diario' && 'funcionarioId' in value.row)
                    setFuncionarioId(value.row.funcionarioId)
            }}
            getRowClassName={getDigitacaoRowClassName}
            error={error.isError}
            pageSize={-1}
        />,
    ]
}
